/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React from "react";
import {
    ToggleButton,
    ToggleButtonGroup,
    Button,
    InputLabel,
    Select,
    MenuItem, Alert,
} from "@mui/material";
import GridSvg from "@/app/components/assets/products/GridSvg";
import ListSvg from "@/app/components/assets/products/ListSvg";
import classes from "@/app/components/algolia/ToolbarTop/algoliaTollbarTopStyles.module.scss";
import {useTranslation} from "next-i18next";
import {storeConfig} from "@/app/store/reducers/storeConfig";
import {useAppDispatch, useAppSelector} from "@/app/hooks/redux";
import {useSortBy, useStats} from "react-instantsearch";
import {transformAlgoliaSortOptions} from "@/app/utils/sortOptionsConfigTransform";
import FacetsActive from "@/app/components/algolia/facets/FacetsActive";
import {TFacet} from "@/app/types/TFacet";

const ToolbarTop = (props: {
    facetsConfig: TFacet[],
    toolbarTopAdditional?: React.ReactNode,
    variant?: "search",
    className?: string
}) => {
    const {t} = useTranslation();
    const {appSettings} = useAppSelector(state => state.storeConfigReducer);
    const {storeConfig: storeConfigData} = useAppSelector(state => state.storeConfigReducer);
    const {updateViewMode, updateSortBy, updateSortDirection, setFiltersActiveMobile} = storeConfig.actions;
    const dispatch = useAppDispatch();
    const {
        nbHits,
        processingTimeMS,
        hitsPerPage,
        page,
    } = useStats();
    const sortOptions = transformAlgoliaSortOptions(storeConfigData?.algolia_instant_search_sorts, props.variant === 'search' ? t('Relevance') : t('Position'));
    const {currentRefinement, options: sortByOptions, refine} = useSortBy({items: sortOptions});
    const noResponseYet = processingTimeMS === 0; // Using processingTimeMS because status initially is not loading

    const gridViewHandler = () => {
        dispatch(updateViewMode('grid'));
    }

    const listViewHandler = () => {
        dispatch(updateViewMode('list'));
    }

    const showFiltersBtnHandler = () => {
        dispatch(setFiltersActiveMobile(true));
    }

    let activeFilters = <FacetsActive facetsConfig={props.facetsConfig} collapsibleOnMobile={true} className={classes['active-filters']} />;

    let showFiltersBtn = <Button className={`${classes['show-filters-btn']} shop-by-btn-mobile`}
                                 onClick={showFiltersBtnHandler}>{t('Shop by')}</Button>;

    const additionalClasses = props.className ?? '';
    const gridClasses = appSettings.catalog.viewMode === 'grid' ? `${classes['view-item']} ${classes['active']}` : `${classes['view-item']}`;
    const listClasses = appSettings.catalog.viewMode === 'list' ? `${classes['view-item']} ${classes['active']} ${classes['active-list']}` : `${classes['view-item']}`;

    let suggestions = <></>;

    let isNoResults = nbHits === 0;
    if(!noResponseYet && isNoResults) {
        return <div className={`${classes['no-results']}`}>
            <Alert className={`${classes['no-results-alert']}`} severity="info">
                {props.variant === 'search' ? t('Your search returned no results.') : t('No products found.')}
            </Alert>
                {suggestions}
                {props.toolbarTopAdditional && props.toolbarTopAdditional}
        </div>
    }

    const startItem = page * (hitsPerPage ?? 0) + 1;
    const lastItem = (startItem + (hitsPerPage ?? 0)) > nbHits ? nbHits : startItem - 1 + (hitsPerPage ?? 0);
    const itemsText = lastItem !== 0 ? `${t('Items')} ${startItem}-${lastItem} ${t('of')} ${nbHits}` : `${nbHits} ${t('Items')}`;

    const viewToggler = <ToggleButtonGroup className={`${classes['view-wrapper']} ${classes['view-toggle']}`} exclusive>
        <ToggleButton onClick={gridViewHandler} value="grid" aria-label={t('Grid view')} className={gridClasses}>
            <GridSvg/>
        </ToggleButton>
        <ToggleButton onClick={listViewHandler} value="list" aria-label={t('List view')} className={listClasses}>
            <ListSvg/>
        </ToggleButton>
    </ToggleButtonGroup>;

    return (
        <>
            <div className={`${classes['toolbar']} ${additionalClasses}`}>
                {props.toolbarTopAdditional && props.toolbarTopAdditional}

                {viewToggler}

                <div className={`${classes['text']}`}>{itemsText}</div>

                {showFiltersBtn}

                <div className={`${classes['sort-wrapper']} toolbar-top-sort-wrapper`}>
                    <InputLabel id="sort-by-label"
                                className={`${classes['sort-by-label']}`}>{t('Sort by')}</InputLabel>

                    <Select
                        name="position"
                        labelId="sort-by-label"
                        id="sort-by"
                        className={`${classes['sort-by-select']}`}
                        MenuProps={{
                            className: classes['sort-by-select-menu'],
                        }}
                        onChange={(event) => refine(event.target.value)}
                        value={currentRefinement}
                    >
                        {sortByOptions && sortByOptions.map((option) => {
                            return <MenuItem key={option.value} value={option.value}>{t(option.label)}</MenuItem>
                        })}
                    </Select>
                </div>
            </div>
            {activeFilters}
            <div className={`${classes['text-mobile']}`}>{viewToggler}{itemsText}</div>
        </>
    )
}

export default ToolbarTop;